import { ClerkProvider } from "@clerk/clerk-react";
import { useEffect, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { trackEvent } from "./lib/analytics";
import AmazonImageRequirementsNotMet from "./pages/blog/AmazonImageRequirementsNotMet";
import AmazonMainImageWhiteBackgroundError from "./pages/blog/AmazonMainImageWhiteBackgroundError";
import AmazonProductImageSize2026 from "./pages/blog/AmazonProductImageSize2026";
import AupayProductImageSize640 from "./pages/blog/AupayProductImageSize640";
import BackgroundRemovalEdgePrecision from "./pages/blog/BackgroundRemovalEdgePrecision";
import BackgroundTransparentFreeNoLimitNoSignup from "./pages/blog/BackgroundTransparentFreeNoLimitNoSignup";
import BackgroundTransparentGuide from "./pages/blog/BackgroundTransparentGuide";
import BackgroundTransparentSiteFree2026 from "./pages/blog/BackgroundTransparentSiteFree2026";
import BlogIndex, { ARTICLES } from "./pages/blog/BlogIndex";
import ImageCropFreeToolsAiSpeed from "./pages/blog/ImageCropFreeToolsAiSpeed";
import MercariImageUploadFailed from "./pages/blog/MercariImageUploadFailed";
import MercariProductImageSize1200x900 from "./pages/blog/MercariProductImageSize1200x900";
import PhotoBackgroundRemoveMethods from "./pages/blog/PhotoBackgroundRemoveMethods";
import ProductImageBackgroundGraySolution from "./pages/blog/ProductImageBackgroundGraySolution";
import Qoo10ProductImageSpec from "./pages/blog/Qoo10ProductImageSpec";
import RakutenProductImageSize2026 from "./pages/blog/RakutenProductImageSize2026";
import RakutenRmsImageSizeError from "./pages/blog/RakutenRmsImageSizeError";
import RemovebgFreeLimitAlternative2026 from "./pages/blog/RemovebgFreeLimitAlternative2026";
import SmartphoneShadowRemovalGuide from "./pages/blog/SmartphoneShadowRemovalGuide";
import TransparentPngBeginnersGuide from "./pages/blog/TransparentPngBeginnersGuide";
import TransparentPngToWhiteJpegAmazon from "./pages/blog/TransparentPngToWhiteJpegAmazon";
import YahooShoppingImageFileSizeError from "./pages/blog/YahooShoppingImageFileSizeError";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Contact from "./pages/Contact";
import Home from "./pages/Home";
import MyPage from "./pages/MyPage";
import Privacy from "./pages/Privacy";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import Terms from "./pages/Terms";
import Tokushoho from "./pages/Tokushoho";

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const ARTICLE_MAP = Object.fromEntries(ARTICLES.map((a) => [a.slug, a.title]));

function LocationTracker() {
	const location = useLocation();
	const prevPathRef = useRef<string | null>(null);

	useEffect(() => {
		if (prevPathRef.current === location.pathname) return;
		prevPathRef.current = location.pathname;

		const blogMatch = location.pathname.match(/^\/blog\/(.+)$/);
		if (blogMatch) {
			const slug = blogMatch[1];
			const title = ARTICLE_MAP[slug];
			if (title) {
				trackEvent("view_article", {
					article_title: title,
					article_slug: slug,
				});
			}
		}
	}, [location.pathname]);

	return null;
}

export function App() {
	return (
		<ClerkProvider
			publishableKey={clerkPubKey}
			signInFallbackRedirectUrl="/mypage?signin=1"
			signUpFallbackRedirectUrl="/mypage?signup=1"
		>
			<HelmetProvider>
				<BrowserRouter>
					<LocationTracker />
					<Routes>
						<Route path="/" element={<Home />} />
						<Route path="/sign-in/*" element={<SignIn />} />
						<Route path="/sign-up/*" element={<SignUp />} />
						<Route path="/contact" element={<Contact />} />
						<Route path="/mypage" element={<MyPage />} />
						<Route path="/checkout/success" element={<CheckoutSuccess />} />
						<Route path="/privacy" element={<Privacy />} />
						<Route path="/terms" element={<Terms />} />
						<Route path="/tokushoho" element={<Tokushoho />} />
						<Route path="/blog" element={<BlogIndex />} />
						<Route
							path="/blog/qoo10-product-image-spec"
							element={<Qoo10ProductImageSpec />}
						/>
						<Route
							path="/blog/amazon-product-image-size-2026"
							element={<AmazonProductImageSize2026 />}
						/>
						<Route
							path="/blog/rakuten-product-image-size-2026"
							element={<RakutenProductImageSize2026 />}
						/>
						<Route
							path="/blog/mercari-product-image-size-1200x900"
							element={<MercariProductImageSize1200x900 />}
						/>
						<Route
							path="/blog/aupay-product-image-size-640"
							element={<AupayProductImageSize640 />}
						/>
						<Route
							path="/blog/amazon-main-image-white-background-error"
							element={<AmazonMainImageWhiteBackgroundError />}
						/>
						<Route
							path="/blog/amazon-image-requirements-not-met"
							element={<AmazonImageRequirementsNotMet />}
						/>
						<Route
							path="/blog/rakuten-rms-image-size-error"
							element={<RakutenRmsImageSizeError />}
						/>
						<Route
							path="/blog/mercari-image-upload-failed"
							element={<MercariImageUploadFailed />}
						/>
						<Route
							path="/blog/yahoo-shopping-image-file-size-error"
							element={<YahooShoppingImageFileSizeError />}
						/>
						<Route
							path="/blog/product-image-background-gray-solution"
							element={<ProductImageBackgroundGraySolution />}
						/>
						<Route
							path="/blog/removebg-free-limit-alternative-2026"
							element={<RemovebgFreeLimitAlternative2026 />}
						/>
						<Route
							path="/blog/transparent-png-to-white-jpeg-amazon"
							element={<TransparentPngToWhiteJpegAmazon />}
						/>
						<Route
							path="/blog/background-removal-edge-precision"
							element={<BackgroundRemovalEdgePrecision />}
						/>
						<Route
							path="/blog/smartphone-shadow-removal-guide"
							element={<SmartphoneShadowRemovalGuide />}
						/>
						<Route
							path="/blog/photo-background-remove-methods"
							element={<PhotoBackgroundRemoveMethods />}
						/>
						<Route
							path="/blog/background-transparent-guide"
							element={<BackgroundTransparentGuide />}
						/>
						<Route
							path="/blog/transparent-png-beginners-guide"
							element={<TransparentPngBeginnersGuide />}
						/>
						<Route
							path="/blog/image-crop-free-tools-ai-speed"
							element={<ImageCropFreeToolsAiSpeed />}
						/>
						<Route
							path="/blog/background-transparent-site-free-top5-2026"
							element={<BackgroundTransparentSiteFree2026 />}
						/>
						<Route
							path="/blog/background-transparent-free-no-limit-no-signup"
							element={<BackgroundTransparentFreeNoLimitNoSignup />}
						/>
					</Routes>
				</BrowserRouter>
			</HelmetProvider>
		</ClerkProvider>
	);
}
